import { Array, pipe } from 'effect'

import type { Cell, CopyMatrix, ScCellValue } from '../../copyMatrix'

export interface FlattenedCopyMatrixColumn {
  cellGroup: Array.NonEmptyArray<{
    value: unknown
    salescoreValue: ScCellValue | null
    height: 1
  }>
  cellsPerParent: [1]
}

export interface FlattenedCopyMatrixRow {
  columns: FlattenedCopyMatrixColumn[]
}

export interface FlattenedCopyMatrix {
  rows: FlattenedCopyMatrixRow[]
}

// 単一セルだけを持つフラットな列データに変換する。
const toFlattenedCopyMatrixColumn = (cell: Cell): FlattenedCopyMatrixColumn => ({
  cellGroup: [
    {
      ...cell,
      height: 1,
    },
  ],
  cellsPerParent: [1],
})

// コピーした行列を、各 cellGroup が 1 要素だけを持つフラットな構造へ変換する。
// 変換後の cellsPerParent は常に [1] になる。
export const flattenCopyMatrix = (copyMatrix: CopyMatrix): FlattenedCopyMatrix => ({
  rows: pipe(
    copyMatrix.rows,
    Array.flatMap((row) => {
      const minCellGroupLength = pipe(
        row.columns,
        Array.map((column) => column.cellGroup.length),
        (lengths) => (lengths.length === 0 ? 0 : Math.min(...lengths)),
      )

      if (minCellGroupLength <= 0) {
        return []
      }

      return pipe(
        Array.range(0, minCellGroupLength - 1),
        Array.map((cellIndex) => ({
          columns: pipe(
            row.columns,
            Array.map((column) => {
              // minCellGroupLength is computed from all columns, so this index should always exist.
              // eslint-disable-next-line security/detect-object-injection -- cellIndex comes from bounded Array.range based on minCellGroupLength
              const cell = column.cellGroup[cellIndex]
              if (cell === undefined) {
                throw new Error('Invariant violation: cell was expected but not found during flattening')
              }

              return toFlattenedCopyMatrixColumn(cell)
            }),
          ),
        })),
      )
    }),
  ),
})
